import React, { useState } from 'react';

import PropTypes from 'prop-types';
import { FiPhone, FiMail, FiUser, FiMessageCircle } from 'react-icons/fi';

import { useTranslation } from 'react-i18next';

import WithBackground from '../Common/WithBackground';
import { PrivacyAcceptance } from '../PrivacyPolicy';

import './style.css';

const WorkerKontakt = ({ bg, overlay }) => {
  const { t } = useTranslation();

  const teamMembers = [
    {
      id: 1,
      key: 'yana',
      name: t('workerKontakt.team.yana.name'),
      position: t('workerKontakt.team.yana.position'),
      department: t('workerKontakt.team.yana.department'),
      phone: t('workerKontakt.team.yana.phone'),
      email: t('workerKontakt.team.yana.email'),
      specialization: t('workerKontakt.team.yana.specialization'),
    },
    {
      id: 2,
      key: 'svetlana',
      name: t('workerKontakt.team.svetlana.name'),
      position: t('workerKontakt.team.svetlana.position'),
      department: t('workerKontakt.team.svetlana.department'),
      phone: t('workerKontakt.team.svetlana.phone'),
      email: t('workerKontakt.team.svetlana.email'),
      specialization: t('workerKontakt.team.svetlana.specialization'),
    },
    {
      id: 3,
      key: 'olena',
      name: t('workerKontakt.team.olena.name'),
      position: t('workerKontakt.team.olena.position'),
      department: t('workerKontakt.team.olena.department'),
      phone: t('workerKontakt.team.olena.phone'),
      email: t('workerKontakt.team.olena.email'),
      specialization: t('workerKontakt.team.olena.specialization'),
    },
  ];

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    message: '',
    acceptPrivacy: false,
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const validateField = (name, value) => {
    const errors = {};

    switch (name) {
      case 'fullName':
        if (!value.trim()) {
          errors.fullName =
            t('validation.nameRequired') || 'Full name is required';
        } else if (value.trim().length < 2) {
          errors.fullName =
            t('validation.nameMinLength') ||
            'Name must be at least 2 characters';
        } else if (value.trim().length > 100) {
          errors.fullName =
            t('validation.nameMaxLength') ||
            'Name must be less than 100 characters';
        }
        break;

      case 'email':
        if (!value.trim()) {
          errors.email = t('validation.emailRequired') || 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          errors.email =
            t('validation.emailInvalid') ||
            'Please enter a valid email address';
        }
        break;

      case 'phone':
        if (!value.trim()) {
          errors.phone =
            t('validation.phoneRequired') || 'Phone number is required';
        } else if (!/^[+]?[\d\s\-()]{8,15}$/.test(value.replace(/\s/g, ''))) {
          errors.phone =
            t('validation.phoneInvalid') || 'Please enter a valid phone number';
        }
        break;

      case 'message':
        if (value.trim() && value.trim().length < 10) {
          errors.message =
            t('validation.messageMinLength') ||
            'Message must be at least 10 characters';
        } else if (value.trim().length > 2000) {
          errors.message =
            t('validation.messageMaxLength') ||
            'Message must be less than 2000 characters';
        }
        break;
    }

    return errors;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));

    if (formErrors[name]) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    if (type !== 'checkbox' && value.trim()) {
      const fieldErrors = validateField(name, value);
      if (Object.keys(fieldErrors).length > 0) {
        setFormErrors((prev) => ({ ...prev, ...fieldErrors }));
      }
    }
  };

  const validateForm = () => {
    const errors = {};

    const requiredFields = ['fullName', 'email', 'phone'];
    requiredFields.forEach((field) => {
      const fieldErrors = validateField(field, formData[field]);
      Object.assign(errors, fieldErrors);
    });

    if (formData.message.trim()) {
      const messageErrors = validateField('message', formData.message);
      Object.assign(errors, messageErrors);
    }

    if (!formData.acceptPrivacy) {
      errors.acceptPrivacy =
        t('validation.privacyRequired') || 'You must accept the privacy policy';
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus(null);

    const errors = validateForm();
    setFormErrors(errors);

    console.log('WorkerKontakt validation errors:', errors);

    if (Object.keys(errors).length > 0) {
      const firstErrorField = document.querySelector(
        '.form-input--error, .form-textarea--error',
      );
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        firstErrorField.focus();
      }
      return;
    }

    setIsSubmitting(true);

    try {
      const emailData = {
        name: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        message: formData.message.trim() || 'Brak dodatkowej wiadomości.',
      };

      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/contact.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(emailData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.status === 'success') {
        setSubmitStatus('success');
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          message: '',
          acceptPrivacy: false,
        });

        console.log('✅ Contact form sent successfully');
      } else {
        throw new Error(result.message || 'Email sending failed');
      }
    } catch (error) {
      setSubmitStatus('error');
      console.error('❌ Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const kontaktContent = (
    <section className='worker-kontakt section' id='contact'>
      <div className='worker-kontakt__container container'>
        <div className='worker-kontakt__header'>
          <h2
            className='worker-kontakt__title'
            data-aos='fade-up'
            data-aos-delay='100'
          >
            {t('workerKontakt.title')}
          </h2>
          <p
            className='worker-kontakt__intro'
            data-aos='fade-up'
            data-aos-delay='200'
          >
            {t('workerKontakt.intro')}
          </p>
        </div>

        <div className='worker-kontakt__content'>
          <div
            className='worker-kontakt__left'
            data-aos='fade-right'
            data-aos-delay='300'
          >
            <div className='worker-kontakt__team'>
              <div className='team-grid'>
                {teamMembers.map((member) => (
                  <div key={member.id} className='contact-card'>
                    <div className='contact-card__header'>
                      <div className='contact-card__avatar'>
                        <FiUser />
                      </div>
                      <div className='contact-card__info'>
                        <h3 className='contact-card__name'>{member.name}</h3>
                        <p className='contact-card__position'>
                          {member.position}
                        </p>
                      </div>
                    </div>

                    <div className='contact-card__details'>
                      <div className='contact-card__item'>
                        <FiPhone />
                        <a
                          href={`tel:${member.phone}`}
                          className='contact-card__link'
                        >
                          {member.phone}
                        </a>
                      </div>

                      <div className='contact-card__item'>
                        <FiMail />
                        <a
                          href={`mailto:${member.email}`}
                          className='contact-card__link'
                        >
                          {member.email}
                        </a>
                      </div>
                    </div>

                    <div className='contact-card__call-button'>
                      <a href={`tel:${member.phone}`} className='btn btn--call'>
                        <FiPhone />
                        {t(`workerKontakt.callButtons.${member.key}`)}
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div
            className='worker-kontakt__right'
            data-aos='fade-left'
            data-aos-delay='400'
          >
            <div className='worker-kontakt__form-section'>
              <div className='form-header'>
                <h3 className='form-header__title'>
                  {t('workerKontakt.formTitle')}
                </h3>
                <p className='form-header__subtitle'>
                  {t('workerKontakt.formSubtitle')}
                </p>
              </div>

              {submitStatus === 'success' && (
                <div className='contact-form__message contact-form__message--success'>
                  <strong>{t('common.success')}</strong>
                  <br />
                  {t('workerKontakt.successMessage')}
                </div>
              )}

              {submitStatus === 'error' && (
                <div className='contact-form__message contact-form__message--error'>
                  <strong>{t('common.error')}</strong>
                  <br />
                  {t('workerKontakt.errorMessage')}
                </div>
              )}

              <form className='contact-form' onSubmit={handleSubmit}>
                <div className='form-row'>
                  <div className='form-group'>
                    <label htmlFor='fullName' className='form-label'>
                      {t('common.fullNameRequired')}
                    </label>
                    <input
                      type='text'
                      id='fullName'
                      name='fullName'
                      value={formData.fullName}
                      onChange={handleInputChange}
                      className={`form-input ${formErrors.fullName ? 'form-input--error' : ''}`}
                      required
                    />
                    {formErrors.fullName && (
                      <span className='form-error'>{formErrors.fullName}</span>
                    )}
                  </div>

                  <div className='form-group'>
                    <label htmlFor='email' className='form-label'>
                      {t('common.emailRequired')}
                    </label>
                    <input
                      type='email'
                      id='email'
                      name='email'
                      value={formData.email}
                      onChange={handleInputChange}
                      className={`form-input ${formErrors.email ? 'form-input--error' : ''}`}
                      required
                    />
                    {formErrors.email && (
                      <span className='form-error'>{formErrors.email}</span>
                    )}
                  </div>
                </div>

                <div className='form-row'>
                  <div className='form-group'>
                    <label htmlFor='phone' className='form-label'>
                      {t('common.phoneRequired')}
                    </label>
                    <input
                      type='tel'
                      id='phone'
                      name='phone'
                      value={formData.phone}
                      onChange={handleInputChange}
                      className={`form-input ${formErrors.phone ? 'form-input--error' : ''}`}
                      required
                    />
                    {formErrors.phone && (
                      <span className='form-error'>{formErrors.phone}</span>
                    )}
                  </div>
                </div>

                <div className='form-group'>
                  <label htmlFor='message' className='form-label'>
                    {t('common.message')} (opcjonalnie)
                  </label>
                  <textarea
                    id='message'
                    name='message'
                    value={formData.message}
                    onChange={handleInputChange}
                    className={`form-textarea ${formErrors.message ? 'form-textarea--error' : ''}`}
                    rows='5'
                    placeholder={t('common.describeYourCase')}
                  ></textarea>
                  {formErrors.message && (
                    <span className='form-error'>{formErrors.message}</span>
                  )}
                </div>

                <div className='form-group form-group--checkbox'>
                  <PrivacyAcceptance
                    checked={formData.acceptPrivacy}
                    onChange={(checked) => {
                      setFormData((prev) => ({
                        ...prev,
                        acceptPrivacy: checked,
                      }));
                      if (formErrors.acceptPrivacy && checked) {
                        setFormErrors((prev) => {
                          const newErrors = { ...prev };
                          delete newErrors.acceptPrivacy;
                          return newErrors;
                        });
                      }
                    }}
                    required
                  />
                  {formErrors.acceptPrivacy && (
                    <span className='form-error'>
                      {formErrors.acceptPrivacy}
                    </span>
                  )}
                </div>

                <div className='form-submit'>
                  <button
                    type='submit'
                    className='form-submit__button btn'
                    disabled={!formData.acceptPrivacy || isSubmitting}
                  >
                    <FiMessageCircle />
                    {isSubmitting
                      ? t('common.sending')
                      : t('common.sendMessage')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );

  return bg ? (
    <WithBackground bg={bg} overlay={overlay}>
      {kontaktContent}
    </WithBackground>
  ) : (
    kontaktContent
  );
};

WorkerKontakt.propTypes = {
  bg: PropTypes.string,
  overlay: PropTypes.oneOf(['dark', 'light', 'golden', 'none']),
};

WorkerKontakt.defaultProps = {
  bg: null,
  overlay: 'dark',
};

export default WorkerKontakt;
